import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { logout, tokenReceived } from '../features/auth/authSlice';
import { refrescarSesion } from './refrescarSesion';
import { RootState } from '../redux/store';
import { settings } from '../settings';
import { GetLogAuditoriaParams, LogAuditoriaItem } from '../pages/Siniestros/types/siniestros.types';
import type { ArchivosRoamGeneradosHoy } from '../pages/GeneracionArchivosSRT/consts/types';

// Completo = calle, altura y localidad cargadas; lo calcula el back (SMP-585)
export type FiltroLugarOcurrencia = 'COMPLETO' | 'INCOMPLETO';

export interface RoamSiniestrosParams {
    nroRoam?: string;
    nroProvisorio?: string;
    idCliente?: number;
    empleado?: string;
    documento?: string;
    cuil?: string;
    nroContrato?: string | number;
    idOrganismoCentral?: number;
    idOrganismo?: number;
    nombreEstablecimiento?: string;
    vencimiento?: string;
    idProvincia?: number;
    idTipoSiniestro?: number;
    notIdTipoSiniestro?: number;
    idsEstado?: number[];
    idPreventor?: number;
    idEstadoSRT?: number;
    // Varios estados SRT a la vez: la tab Siniestros de Gestión de Archivos SRT (SMP-561)
    idsEstadoSRT?: number[];
    tieneVisitaInvestigacion?: boolean;
    anioSiniestro?: number;
    // Sin valor = Todos (SMP-585)
    lugarOcurrencia?: FiltroLugarOcurrencia;
    orderby?: string;
    skip?: number;
    take?: number;
}

export interface RoamCancelarSiniestroBody {
    idMotivoCancelacion: number;
}

export interface RoamMotivoCancelacionSiniestro {
    id: number;
    descripcion: string;
}

export interface RoamGuardarLugarOcurrenciaBody {
    calle: string;
    altura: string;
    piso: string;
    depto: string;
    codigoPostal: string;
    idLocalidad: number;
}

export interface RoamPagination<T> {
    totalCount: number;
    skip: number;
    take: number;
    count: number;
    data: T[];
}

export type RoamSiniestroRaw = Record<string, any>;
export type RoamVisitaRaw = Record<string, any>;

interface RoamResponse<T> {
    data: T;
}

export interface RoamSrtParametricItem {
    id: number;
    descripcion: string;
    nombre?: string;
    codigo?: string | number;
    codigoAseguradora?: string | number;
}

export interface RoamSrtParametricData {
    gruposFiscalizacion: RoamSrtParametricItem[];
    nacionalidades: RoamSrtParametricItem[];
    ciou: RoamSrtParametricItem[];
    tiposContrato: RoamSrtParametricItem[];
    tiposLugares: RoamSrtParametricItem[];
    zonasCuerpo: RoamSrtParametricItem[];
    naturalezaLesiones: RoamSrtParametricItem[];
    formasAccidentes: RoamSrtParametricItem[];
    agentesCausantes: RoamSrtParametricItem[];
    tiposObras: RoamSrtParametricItem[];
    actividadesObras: RoamSrtParametricItem[];
    etapasObras: RoamSrtParametricItem[];
    encuadresProgramas: RoamSrtParametricItem[];
    programasSeguridadObra: RoamSrtParametricItem[];
    organizaciones: RoamSrtParametricItem[];
}

export interface RoamSrtFormularioTrabajadorPayload {
    nroSiniestro?: string | null;
    nroDenuncia?: string | null;
    empleador?: string | null;
    cuit?: string | null;
    nombre?: string | null;
    apellido?: string | null;
    cuil?: string | null;
    fechaAccidente?: string | null;
    horaAccidente?: string | null;
    descripcion?: string | null;
    fechaNacimiento?: string | null;
    sexo?: boolean | null;
    idNacionalidad?: number | null;
    otrosNacionalidad?: string | null;
    telefono?: string | null;
    calle?: string | null;
    idLocalidad?: number | null;
    descripcionCiuo?: string | null;
    idCiuo?: number | null;
    antiguedadUnidad?: boolean | null;
    antiguedadValor?: number | null;
    descripcionTipoContrato?: string | null;
    idTipoContrato?: number | null;
}

export interface RoamSrtFormularioPayload {
    trabajador?: Partial<RoamSrtFormularioTrabajadorPayload>;
    empleador?: Record<string, any>;
    lugar?: Record<string, any>;
    codificacion?: Record<string, any>;
    accidente?: Record<string, any>;
    responsable?: Record<string, any>;
    testigos?: Record<string, any>[];
    causasMedidas?: Record<string, any>[];
    otrosAfectados?: Record<string, any>[];
    medidasSeguimiento?: Record<string, any>[];
    seguimientos?: Record<string, any>[];
    entrevistados?: Record<string, any>[];
    // Formulario con todos los obligatorios: en los siniestros sin visitas define el Estado
    // SRT (SMP-610)
    completado?: boolean;
}

export interface RoamSrtFormularioMutationResponse {
    id: number;
}

export interface RoamSrtFormularioArchivoResponse {
    presignedUrl: string;
}

export interface RoamExportGestionSrtResponse {
    presignedUrl: string;
    nombreArchivo: string;
}

export interface RoamArchivoSiniestroResponse {
    presignedUrl: string;
    nombre: string;
}

const cleanParams = (params: Record<string, any>) =>
    Object.fromEntries(
        Object.entries(params).filter(([, value]) => {
            if (Array.isArray(value)) {
                return value.length > 0;
            }

            return value !== undefined && value !== null && value !== '';
        })
    );

const baseQuery = fetchBaseQuery({
    baseUrl: `${settings.url.host}${settings.url.api}`,
    credentials: 'include',
    prepareHeaders: (headers, { getState }) => {
        const accessToken = (getState() as RootState).auth.accessToken;
        if (accessToken) {
            headers.set('authorization', `Bearer ${accessToken}`);
        }
        return headers;
    }
});

const baseQueryWithReauth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
    args,
    api,
    extraOptions
) => {
    let result: any = await baseQuery(args, api, extraOptions);

    if (result.error && result.error.status === 401) {
        const refreshResult = await refrescarSesion(baseQuery, api, extraOptions);

        if (refreshResult?.data) {
            api.dispatch(tokenReceived(refreshResult.data));
            result = await baseQuery(args, api, extraOptions);
        } else {
            api.dispatch(logout());
        }
    }

    return result;
};

export const roamApi = createApi({
    reducerPath: 'roamApi',
    tagTypes: [
        'RoamSiniestros',
        'RoamSiniestro',
        'RoamVisitas',
        'RoamSrtParametrics',
        'RoamMotivosCancelacion',
        'GeneratedRoamFilesToday'
    ] as const,
    baseQuery: baseQueryWithReauth,
    endpoints: (builder) => ({
        getSiniestros: builder.query<RoamPagination<RoamSiniestroRaw>, RoamSiniestrosParams | void>(
            {
                query: (params) => ({
                    url: '/roam/siniestros',
                    method: 'GET',
                    params: cleanParams(params || {})
                }),
                providesTags: ['RoamSiniestros'],
                transformResponse: (response: RoamResponse<RoamPagination<RoamSiniestroRaw>>) =>
                    response?.data,
                keepUnusedDataFor: 5
            }
        ),
        getSiniestroById: builder.query<RoamSiniestroRaw, number>({
            query: (id) => ({
                url: `/roam/siniestros/${id}`,
                method: 'GET'
            }),
            providesTags: (_result, _error, id) => [{ type: 'RoamSiniestro', id }],
            transformResponse: (response: RoamResponse<RoamSiniestroRaw>) => response?.data,
            keepUnusedDataFor: 5
        }),
        getSiniestroVisitas: builder.query<RoamVisitaRaw[], number>({
            query: (id) => ({
                url: `/roam/siniestros/${id}/visitas`,
                method: 'GET'
            }),
            providesTags: (_result, _error, id) => [{ type: 'RoamVisitas', id }],
            transformResponse: (response: RoamResponse<RoamVisitaRaw[]>) => response?.data ?? [],
            keepUnusedDataFor: 5
        }),
        getSrtParametricData: builder.query<RoamSrtParametricData, void>({
            query: () => ({
                url: '/roam/siniestros/formularios-srt/datos-parametricos',
                method: 'GET'
            }),
            providesTags: ['RoamSrtParametrics'],
            transformResponse: (response: RoamResponse<RoamSrtParametricData>) =>
                response?.data ?? (response as unknown as RoamSrtParametricData),
            keepUnusedDataFor: 5
        }),
        getMotivosCancelacionSiniestro: builder.query<RoamMotivoCancelacionSiniestro[], void>({
            query: () => ({
                url: '/roam/siniestros/motivos-cancelacion',
                method: 'GET'
            }),
            providesTags: ['RoamMotivosCancelacion'],
            transformResponse: (response: RoamResponse<RoamMotivoCancelacionSiniestro[]>) =>
                response?.data ?? [],
            keepUnusedDataFor: 300
        }),
        getSrtFormulario: builder.query<any, { idSiniestro: number; idFormulario: number }>({
            query: ({ idSiniestro, idFormulario }) => ({
                url: `/roam/siniestros/${idSiniestro}/formularios-srt/${idFormulario}`,
                method: 'GET'
            }),
            transformResponse: (response: RoamResponse<any>) => response?.data ?? response,
            providesTags: (_result, _error, { idSiniestro }) => [
                { type: 'RoamSiniestro', id: idSiniestro }
            ],
            keepUnusedDataFor: 5
        }),
        createSrtFormulario: builder.mutation<
            RoamSrtFormularioMutationResponse,
            { idSiniestro: number; body: RoamSrtFormularioPayload }
        >({
            query: ({ idSiniestro, body }) => ({
                url: `/roam/siniestros/${idSiniestro}/formularios-srt`,
                method: 'POST',
                body
            }),
            transformResponse: (response: RoamResponse<RoamSrtFormularioMutationResponse>) =>
                response?.data,
            invalidatesTags: (_result, error, { idSiniestro }) =>
                error ? [] : ['RoamSiniestros', { type: 'RoamSiniestro', id: idSiniestro }]
        }),
        patchSrtFormulario: builder.mutation<
            RoamSrtFormularioMutationResponse,
            { idSiniestro: number; idFormulario: number; body: RoamSrtFormularioPayload }
        >({
            query: ({ idSiniestro, idFormulario, body }) => ({
                url: `/roam/siniestros/${idSiniestro}/formularios-srt/${idFormulario}`,
                method: 'PATCH',
                body
            }),
            transformResponse: (response: RoamResponse<RoamSrtFormularioMutationResponse>) =>
                response?.data,
            invalidatesTags: (_result, error, { idSiniestro }) =>
                error ? [] : ['RoamSiniestros', { type: 'RoamSiniestro', id: idSiniestro }]
        }),
        exportSiniestros: builder.mutation<{ type: string; data: number[] }, Record<string, any>>({
            query: (body) => ({
                url: '/roam/siniestros/exportar',
                method: 'POST',
                body: cleanParams(body)
            })
        }),
        // Mismo endpoint con tipoDocumento 'siniestro': Excel de Gestión de Archivos SRT,
        // que se sube a S3 y vuelve como URL prefirmada, igual que RGRL y Visitas (SMP-625)
        exportSiniestrosGestionSrt: builder.mutation<
            { data: RoamExportGestionSrtResponse },
            Record<string, any>
        >({
            query: (body) => ({
                url: '/roam/siniestros/exportar',
                method: 'POST',
                body: cleanParams({ ...body, tipoDocumento: 'siniestro' })
            })
        }),
        exportSrtFormularioArchivo: builder.mutation<
            RoamSrtFormularioArchivoResponse,
            { idsAccidenteLaboral: number[] }
        >({
            query: (body) => ({
                url: '/roam/siniestros/formularios-srt/archivos',
                method: 'POST',
                body
            }),
            transformResponse: (response: RoamResponse<RoamSrtFormularioArchivoResponse>) =>
                response?.data,
            // Refresca el contador "Archivos generados hoy" de Gestión de Archivos SRT.
            invalidatesTags: ['GeneratedRoamFilesToday']
        }),
        // Archivos SRT de ROAM generados en el día, total y por cliente (SMP-558).
        getGeneratedRoamFilesToday: builder.query<ArchivosRoamGeneradosHoy, void>({
            query: () => ({
                url: '/roam/archivos-srt/generados-hoy',
                method: 'GET'
            }),
            providesTags: ['GeneratedRoamFilesToday'],
            transformResponse: (response: RoamResponse<ArchivosRoamGeneradosHoy>) => response?.data
        }),
        // TXT de la SRT de un solo siniestro: no lo exporta ni cambia su estado (SMP-561)
        descargarArchivoSiniestro: builder.mutation<RoamArchivoSiniestroResponse, number>({
            query: (idSiniestro) => ({
                url: `/roam/siniestros/${idSiniestro}/archivo-srt`,
                method: 'GET'
            }),
            transformResponse: (response: RoamResponse<RoamArchivoSiniestroResponse>) =>
                response?.data
        }),
        cancelarSiniestro: builder.mutation<void, { id: number; body: RoamCancelarSiniestroBody }>({
            query: ({ id, body }) => ({
                url: `/roam/siniestros/${id}/cancelacion`,
                method: 'PATCH',
                body
            }),
            invalidatesTags: (_result, error, { id }) =>
                error
                    ? []
                    : ['RoamSiniestros', { type: 'RoamSiniestro', id }, { type: 'RoamVisitas', id }]
        }),
        finalizarSiniestro: builder.mutation<void, number>({
            query: (id) => ({
                url: `/roam/siniestros/${id}/finalizacion`,
                method: 'PATCH'
            }),
            invalidatesTags: (_result, error, id) =>
                error
                    ? []
                    : ['RoamSiniestros', { type: 'RoamSiniestro', id }, { type: 'RoamVisitas', id }]
        }),
        guardarLugarOcurrencia: builder.mutation<
            void,
            { id: number; body: RoamGuardarLugarOcurrenciaBody }
        >({
            query: ({ id, body }) => ({
                url: `/roam/siniestros/${id}/lugar-ocurrencia`,
                method: 'PUT',
                body
            }),
            invalidatesTags: (_result, error, { id }) =>
                error
                    ? []
                    : ['RoamSiniestros', { type: 'RoamSiniestro', id }, { type: 'RoamVisitas', id }]
        }),
        getLogAuditoriaSiniestro: builder.query<
            LogAuditoriaItem[],
            { idSiniestroRoam: number; params?: GetLogAuditoriaParams }
        >({
            query: ({ idSiniestroRoam, params }) => ({
                url: `/roam/siniestros/${idSiniestroRoam}/log-auditoria`,
                method: 'GET',
                params: params ? cleanParams(params as Record<string, any>) : undefined
            }),
            transformResponse: (response: RoamResponse<LogAuditoriaItem[]>) =>
                response?.data ?? [],
            keepUnusedDataFor: 30
        })
    })
});

export const {
    useGetSiniestrosQuery,
    useLazyGetSiniestrosQuery,
    useGetSiniestroByIdQuery,
    useLazyGetSiniestroByIdQuery,
    useGetSiniestroVisitasQuery,
    useGetMotivosCancelacionSiniestroQuery,
    useGetSrtFormularioQuery,
    useGetSrtParametricDataQuery,
    useLazyGetSiniestroVisitasQuery,
    useCreateSrtFormularioMutation,
    usePatchSrtFormularioMutation,
    useExportSiniestrosMutation,
    useExportSiniestrosGestionSrtMutation,
    useExportSrtFormularioArchivoMutation,
    useGetGeneratedRoamFilesTodayQuery,
    useDescargarArchivoSiniestroMutation,
    useCancelarSiniestroMutation,
    useFinalizarSiniestroMutation,
    useGuardarLugarOcurrenciaMutation,
    useGetLogAuditoriaSiniestroQuery
} = roamApi;
